// src/componentes/AdDeuda/AgregarDeuda.jsx
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";

import { db } from "../../Firebase/firebaseconfig";
import { nextFolioDeuda } from "../../helpers/folio";
import notify from "../common/notify";
import "./AgregarDeuda.css";
import { useUserContext } from "../GestionDeUsuarios/UserContext";
import AccessDenied from "../Auth/AccessDenied";
import { registrarAuditoria } from "../../helpers/auditoria";
import SeccionComision from "../Comisiones/SeccionComision";
import { crearComision } from "../../services/comisionesService";
import { toJsDate, hoyLocalISO } from "../../helpers/fechas";
import { processPayment } from "../../services/deudaServices";
import { procesarDevengoComision } from "../../services/procesarDevengoComision";
import {
  crearApartado,
  getApartado,
  marcarApartadoComoConvertido,
} from "../../services/apartadosService";

export default function AgregarDeuda() {
  const { id } = useParams(); // clienteId
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apartadoIdParam = searchParams.get("apartadoId") || "";
  const { can } = useUserContext();

  const [cliente, setCliente] = useState(null);

  // Si se llega con ?apartadoId=X, se está CONVIRTIENDO un apartado a deuda.
  // El modo se fuerza a "deuda" y las propiedades vienen pre-cargadas.
  const [apartadoOrigen, setApartadoOrigen] = useState(null);
  const enModoConversion = Boolean(apartadoIdParam);

  const [form, setForm] = useState({
    // ── Modo de operación ──────────────────────────────────────────────────
    // "deuda"    → venta/financiamiento normal
    // "apartado" → reserva de 30 días, sin financiamiento todavía
    modoAlta: "deuda",

    // ── Enganche al firmar (solo modo "deuda") ─────────────────────────────
    // "diferido"  → se cobrará como pago separado en otro momento
    // "al_firmar" → cobrado ahora, se registra el pago y se dispara el devengo
    enganchemodo: "diferido",
    metodoEnganche: "efectivo",
    // Sprint ENG-FIX: si el cobrador captura "diferido" y el enganche ya
    // fue cobrado por fuera del sistema, marcar este checkbox para que se
    // sume al total pagado. Si no se marca, el enganche queda como
    // pactado pendiente y NO se resta del saldo.
    engancheCobradoFuera: false,

    // ── Campos estándar ───────────────────────────────────────────────────
    concepto: "",
    monto: "",
    enganche: "",
    mensualidad: "",
    plazo: "",
    fecha: "",
    referencia: "",
    fueraPais: false,
    responsableId: "",

    // ── Apartado ──────────────────────────────────────────────────────────
    notas: "",
    // Señal (sólo aplica en modo apartado): depósito que se recibe ahora
    senialMonto: "",
    senialMetodo: "efectivo",
    senialReferencia: "",
  });

  const [saving, setSaving] = useState(false);

  // ── Comisión (estado del hijo SeccionComision) ──────────────
  const comisionDataRef = useRef({
    activa: false,
    comisionistaId: "",
    comisionistaNombre: "",
    tipoCalculo: "porcentaje",
    porcentaje: 0.03,
    montoFijo: 0,
    esquema: "enganche-50-mensualidades-50",
    mensualidadesN: 5,
    hitosCustom: [],
  });

  const [clientes, setClientes] = useState([]);
  const [searchResp, setSearchResp] = useState("");

  const [propiedades, setPropiedades] = useState([]);
  const [propQuery, setPropQuery] = useState("");
  const [propSelList, setPropSelList] = useState([]);

  // cargar cliente + clientes responsables en paralelo
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const [clienteSnap, clientesSnap] = await Promise.all([
          getDoc(doc(db, "clientes", id)),
          getDocs(collection(db, "clientes")),
        ]);

        if (!isMounted) return;

        if (clienteSnap.exists()) {
          setCliente({ id: clienteSnap.id, ...clienteSnap.data() });
        }

        setClientes(clientesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Error cargando datos iniciales:", err);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [id]);

  // propiedades disponibles
  useEffect(() => {
    const q = query(
      collection(db, "propiedades"),
      where("activo", "==", true),
      where("disponible", "==", true)
    );

    const unsub = onSnapshot(q, (snap) => {
      setPropiedades(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return unsub;
  }, []);

  // Pre-carga del apartado origen cuando se entra en modo conversión.
  // Las propiedades apartadas NO aparecen en el listener (filtran por
  // disponible:true), así que las inyectamos directamente a la selección.
  useEffect(() => {
    if (!apartadoIdParam) return;
    let alive = true;
    (async () => {
      try {
        const apar = await getApartado(apartadoIdParam);
        if (!alive) return;
        if (!apar) {
          notify.error("Apartado no encontrado.");
          return;
        }
        if (apar.clienteId !== id) {
          notify.error("El apartado no pertenece a este cliente.");
          return;
        }
        if (apar.estado !== "activo") {
          notify.error(`El apartado está en estado '${apar.estado}' y no puede convertirse.`);
          return;
        }
        setApartadoOrigen(apar);

        // Cargar datos completos de las propiedades del apartado para llenarlos
        // en la selección con todos los campos (proyectoId, fraccionamientoId, etc.)
        const propIds = Array.isArray(apar.propiedadIds) ? apar.propiedadIds : [];
        const propDocs = await Promise.all(
          propIds.map((pid) => getDoc(doc(db, "propiedades", pid)).catch(() => null))
        );
        const propsCompletas = [];
        for (const s of propDocs) {
          if (s?.exists()) propsCompletas.push({ id: s.id, ...s.data() });
        }
        if (alive && propsCompletas.length > 0) {
          setPropSelList(propsCompletas);
        }

        // Pre-llenar TODOS los campos desde la venta proyectada del apartado.
        // Si el apartado fue creado antes del rediseño, ventaProyectada será null:
        // fallback a los defaults del form (sin romper).
        const vp = apar.ventaProyectada || {};
        setForm((prev) => ({
          ...prev,
          modoAlta: "deuda",
          concepto: apar.concepto || prev.concepto,
          notas: apar.notas || prev.notas,
          monto:        vp.monto != null ? String(vp.monto) : prev.monto,
          enganche:     vp.enganche != null ? String(vp.enganche) : prev.enganche,
          mensualidad:  vp.mensualidad != null ? String(vp.mensualidad) : prev.mensualidad,
          plazo:        vp.plazo != null ? String(vp.plazo) : prev.plazo,
          fecha:        vp.fechaInicio || prev.fecha,
          referencia:   vp.referencia || prev.referencia,
          fueraPais:    typeof vp.fueraPaisRegularmente === "boolean"
                          ? vp.fueraPaisRegularmente
                          : prev.fueraPais,
          responsableId: vp.responsableId || prev.responsableId,
          enganchemodo: vp.enganchemodo || prev.enganchemodo,
          metodoEnganche: vp.metodoEnganche || prev.metodoEnganche,
        }));

        // Pre-llenar SeccionComision a través del ref (el sub-componente
        // lee initialData al montar). Si el apartado no traía comisión,
        // se deja el default.
        if (apar.comisionProyectada?.activa) {
          comisionDataRef.current = {
            activa: true,
            comisionistaId: apar.comisionProyectada.comisionistaId || "",
            comisionistaNombre: apar.comisionProyectada.comisionistaNombre || "",
            tipoCalculo: apar.comisionProyectada.tipoCalculo || "porcentaje",
            porcentaje: Number(apar.comisionProyectada.porcentaje || 0.03),
            montoFijo: Number(apar.comisionProyectada.montoFijo || 0),
            esquema: apar.comisionProyectada.esquema || "enganche-50-mensualidades-50",
            mensualidadesN: Number(apar.comisionProyectada.mensualidadesN || 5),
            hitosCustom: apar.comisionProyectada.hitosCustom || [],
          };
        }
      } catch (err) {
        console.error("Error cargando apartado:", err);
        notify.error("No se pudo cargar el apartado.");
      }
    })();
    return () => { alive = false; };
  }, [apartadoIdParam, id]);

  // responsables filtrados
  const responsablesFiltrados = useMemo(() => {
    const t = searchResp.trim().toLowerCase();
    if (!t) return clientes.slice(0, 12);

    return clientes
      .filter((c) => {
        const nombre = `${c.nombre || ""} ${c.apellidos || ""}`.toLowerCase();
        const telefono = `${c.telefono || ""}`.toLowerCase();
        const idTxt = `${c.id || ""}`.toLowerCase();
        return (
          nombre.includes(t) ||
          telefono.includes(t) ||
          idTxt.includes(t)
        );
      })
      .slice(0, 12);
  }, [clientes, searchResp]);

  // filtro propiedades memoizado
  const filteredProps = useMemo(() => {
    const t = (propQuery || "").trim().toLowerCase();
    if (!t) return propiedades.slice(0, 12);

    return propiedades
      .filter((p) => {
        const folio = (p.folio || "").toLowerCase();
        const ubicacion = (p.ubicacion || "").toLowerCase();
        const nombre = (p.nombre || "").toLowerCase();
        const alias = (p.alias || "").toLowerCase();
        const codigoHumano = (p.codigoHumano || "").toLowerCase();
        const mzlt = `mz ${p.manzana ?? ""} lt ${p.lote ?? ""}`.toLowerCase();

        return (
          folio.includes(t) ||
          ubicacion.includes(t) ||
          nombre.includes(t) ||
          alias.includes(t) ||
          codigoHumano.includes(t) ||
          mzlt.includes(t)
        );
      })
      .slice(0, 12);
  }, [propQuery, propiedades]);

  const onChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === "checkbox" ? checked : value;

    setForm((f) => {
      const next = { ...f, [name]: finalValue };

      if (name === "fueraPais" && !checked) {
        next.responsableId = "";
      }

      return next;
    });

    if (name === "fueraPais" && !checked) {
      setSearchResp("");
    }
  }, []);

  const selectResponsable = useCallback((c) => {
    setForm((f) => ({ ...f, responsableId: c.id }));
    setSearchResp(`${c.nombre || ""} ${c.apellidos || ""}`.trim() || c.id);
  }, []);

  const removeProp = useCallback((propId) => {
    setPropSelList((prev) => prev.filter((x) => x.id !== propId));
  }, []);

  const selectProp = useCallback((p) => {
    setPropSelList((prev) => {
      const yaExiste = prev.some((x) => x.id === p.id);
      let next;

      if (yaExiste) {
        next = prev.filter((x) => x.id !== p.id);
      } else {
        next = [...prev, p];

        if (prev.length === 0) {
          const alias =
            p.alias ||
            p.nombre ||
            p.codigoHumano ||
            `Mz ${p.manzana} Lt ${p.lote}`;

          const tipo = p.tipo || "lote";
          const monto = p.precioFinanciado ?? p.precioContado ?? p.precio ?? "";

          setForm((f) => ({
            ...f,
            concepto: `Venta de ${tipo} — ${alias} (${p.folio})`,
            monto: String(monto),
          }));

          setPropQuery(`${p.folio} — ${p.ubicacion || ""}`);
        }
      }

      return next;
    });
  }, []);

  // Fecha de vencimiento del apartado (hoy + 30 días), para mostrar en UI
  const fechaVencimientoApartado = useMemo(() => {
    const v = new Date();
    v.setDate(v.getDate() + 30);
    return v.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }, []);

  const guardar = useCallback(
    async (e) => {
      e.preventDefault();

      if (!can("deudas.crear")) return;
      if (saving) return;

      if (!propSelList.length) {
        notify.error("Selecciona una propiedad");
        return;
      }

      setSaving(true);

      // ════════════════════════════════════════════════════════════════════════
      // RAMA APARTADO — guarda TODOS los datos como venta proyectada
      // ════════════════════════════════════════════════════════════════════════
      if (form.modoAlta === "apartado") {
        try {
          const nombreCliente =
            `${cliente?.nombre || ""} ${cliente?.apellidos || ""}`.trim();

          // Snapshot completo de la venta planeada — pre-llenado al convertir
          const ventaProyectada = {
            monto: Number(form.monto) || 0,
            enganche: Number(form.enganche) || 0,
            mensualidad: Number(form.mensualidad) || 0,
            plazo: Number(form.plazo) || 0,
            fechaInicio: form.fecha || "",
            referencia: form.referencia || "",
            fueraPaisRegularmente: !!form.fueraPais,
            responsableId: form.fueraPais ? (form.responsableId || "") : "",
            enganchemodo: form.enganchemodo || "diferido",
            metodoEnganche: form.metodoEnganche || "efectivo",
          };

          // Snapshot de comisión (incluso si activa=false, se preserva)
          const cd = comisionDataRef.current;
          const comisionProyectada = cd?.activa
            ? {
                activa: true,
                comisionistaId: cd.comisionistaId,
                comisionistaNombre: cd.comisionistaNombre,
                tipoCalculo: cd.tipoCalculo,
                porcentaje: Number(cd.porcentaje || 0),
                montoFijo: Number(cd.montoFijo || 0),
                esquema: cd.esquema,
                mensualidadesN: Number(cd.mensualidadesN || 0),
                hitosCustom: cd.hitosCustom || [],
              }
            : null;

          // Señal (opcional)
          const senialMontoNum = Number(form.senialMonto) || 0;
          const senial = senialMontoNum > 0
            ? {
                monto: senialMontoNum,
                metodo: form.senialMetodo || "efectivo",
                referencia: form.senialReferencia || "",
                fecha: form.fecha || hoyLocalISO(),
                notas: form.notas || "",
              }
            : null;

          const result = await crearApartado({
            clienteId: id,
            clienteNombre: nombreCliente,
            propiedades: propSelList,
            concepto: form.concepto,
            notas: form.notas,
            ventaProyectada,
            comisionProyectada,
            senial,
          });

          const msgSenial = result.cajaMovimientoSenialId
            ? ` Señal registrada en caja.`
            : "";
          notify.success(
            `Propiedad apartada por 30 días. Vence: ${fechaVencimientoApartado}.${msgSenial}`
          );
          navigate(`/app/cliente/${id}`);
        } catch (err) {
          console.error("Error creando apartado:", err);
          notify.error(err.message || "No se pudo apartar la propiedad.");
          setSaving(false);
        }
        return;
      }

      // ════════════════════════════════════════════════════════════════════════
      // RAMA DEUDA (flujo normal)
      // ════════════════════════════════════════════════════════════════════════
      const monto = Number(form.monto);
      const enganche = Number(form.enganche);
      const mensualidad = Number(form.mensualidad);
      const plazo = Number(form.plazo);

      if (!form.fecha) {
        notify.error("Selecciona una fecha");
        setSaving(false);
        return;
      }

      if (!Number.isFinite(monto) || monto <= 0) {
        notify.error("Monto inválido");
        setSaving(false);
        return;
      }

      if (!Number.isFinite(enganche) || enganche < 0) {
        notify.error("Enganche inválido");
        setSaving(false);
        return;
      }

      if (!Number.isFinite(mensualidad) || mensualidad < 0) {
        notify.error("Mensualidad inválida");
        setSaving(false);
        return;
      }

      if (!Number.isFinite(plazo) || plazo < 0) {
        notify.error("Plazo inválido");
        setSaving(false);
        return;
      }

      // Parseo local explícito para evitar el shift de zona horaria:
      // `new Date("2026-05-30")` se interpreta como UTC midnight → en MX (UTC-6)
      // sería 2026-05-29 18:00 local y al renderizar saldría "un día antes".
      const [_y, _m, _d] = String(form.fecha || "").split("-").map(Number);
      const fechaBase =
        _y && _m && _d ? new Date(_y, _m - 1, _d) : new Date(NaN);
      if (Number.isNaN(fechaBase.getTime())) {
        notify.error("Fecha inválida");
        setSaving(false);
        return;
      }
      fechaBase.setHours(0, 0, 0, 0);

      try {
        const fechaInicio = Timestamp.fromDate(fechaBase);
        const lastPaymentDate = Timestamp.fromDate(fechaBase);

        const prox = new Date(fechaBase);
        prox.setMonth(prox.getMonth() + 1);
        const nextDueDate = Timestamp.fromDate(prox);

        const { folio } = await nextFolioDeuda();

        let deudaCreadaId = "";
        let propPrincipalFull = null; // captura datos completos de la propiedad para contexto de comisión

        await runTransaction(db, async (tx) => {
          const propsSnap = [];

          for (const sel of propSelList) {
            const pRef = doc(db, "propiedades", sel.id);
            const pDoc = await tx.get(pRef);

            if (!pDoc.exists()) {
              throw new Error("Propiedad no existe");
            }

            const pdata = pDoc.data();

            // Si NO estamos convirtiendo un apartado, la propiedad debe estar disponible.
            // Si SÍ estamos convirtiendo, aceptamos propiedades apartadas POR ESTE apartado
            // (defensa: rechazamos si la propiedad está apartada por otro apartado distinto).
            if (pdata.disponible === false) {
              const aparteCoincide =
                enModoConversion &&
                apartadoOrigen &&
                pdata.apartadoId === apartadoOrigen.id;
              if (!aparteCoincide) {
                throw new Error(`Propiedad ${pdata.folio || pDoc.id} no disponible`);
              }
            }

            propsSnap.push({ id: pDoc.id, ...pdata });
          }

          // Guardar la propiedad principal con TODOS sus campos para uso posterior
          // (proyectoId, proyectoNombre, fraccionamientoId, fraccNombre — contexto ERP)
          if (propsSnap.length > 0) propPrincipalFull = propsSnap[0];

          const propsPayload = propsSnap.map((p) => ({
            propiedadId: p.id,
            folio: p.folio,
            codigo: p.codigoHumano,
            nombre: p.nombre,
            fraccionamientoId: p.fraccionamientoId,
            manzana: p.manzana,
            lote: p.lote,
          }));

          const propPrincipal = propsPayload[0];
          const dRef = doc(collection(db, "deudas"));
          deudaCreadaId = dRef.id;

          const deuda = {
            folio,

            clienteId: id,
            clienteNombre: cliente?.nombre || "",

            importe: monto,
            monto,
            enganche,
            mensualidad,
            plazo,

            // Sprint ENG-FIX: convención nueva — el enganche del doc solo
            // se resta del saldo si el cobrador marca engancheCobradoFuera
            // o si se crea un pago tipo:"enganche" (modo "al_firmar" lo
            // hace abajo via processPayment). Por defecto el saldo inicial
            // es el monto completo, y processPayment lo ajusta cuando
            // registre el pago del enganche.
            engancheCobradoFuera: !!form.engancheCobradoFuera,
            saldo: form.engancheCobradoFuera ? monto - enganche : monto,
            saldoRestante: form.engancheCobradoFuera ? monto - enganche : monto,

            concepto: form.concepto,
            referencia: form.referencia || "",

            fueraPaisRegularmente: !!form.fueraPais,
            responsableId: form.fueraPais ? form.responsableId || "" : "",

            propiedadId: propPrincipal.propiedadId,
            propiedades: propsPayload,
            propiedadIds: propsPayload.map((p) => p.propiedadId),

            fechaInicio,
            fechaInicioDeuda: fechaInicio,
            fechaContrato: fechaInicio,

            lastPaymentDate,
            nextDueDate,

            creada: Timestamp.now(),

            estatus: "activa",
            activo: true,
          };

          tx.set(dRef, deuda);

          for (const p of propsSnap) {
            const pRef = doc(db, "propiedades", p.id);

            tx.update(pRef, {
              estado: "vendida",
              disponible: false,
              asociadaADeuda: true,

              clienteId: id,
              deudaId: dRef.id,
              deudaFolio: folio,

              // Limpiar metadata de apartado al transicionar a vendida
              // (evita propiedades que arrastran apartadoId huérfano).
              apartadoId: null,
              apartadoVence: null,

              actualizado: serverTimestamp(),
            });
          }
        });

        await registrarAuditoria({
          accion: "deudas.crear",
          entidadTipo: "deuda",
          entidadId: deudaCreadaId,
          detalle: `Cliente ${id}`,
          despues: {
            concepto: form.concepto,
            monto,
            enganche,
            mensualidad,
            plazo,
            propiedadIds: propSelList.map((p) => p.id),
            enganchemodo: form.enganchemodo,
          },
        });

        // ── Crear comisión si se activó ────────────────────────────────────
        const cd = comisionDataRef.current;
        if (cd.activa && cd.comisionistaId) {
          // El plan manual debe cuadrar (suma = 100% o = total). Si no,
          // avisamos y NO creamos la comisión — la deuda ya quedó creada.
          if (cd.planValido === false) {
            notify.warn(
              "Deuda creada, pero la comisión no se registró: el plan de devengo no cuadra. Edita la deuda para reintentarlo."
            );
          } else {
          const propPrincipal = propSelList[0];
          try {
            await crearComision({
              deudaId: deudaCreadaId,
              clienteId: id,
              clienteNombre: cliente?.nombre
                ? `${cliente.nombre} ${cliente.apellidos || ""}`.trim()
                : "",
              // Identificadores (para que la CxP devengada afecte la utilidad de
              // la propiedad / fraccionamiento / proyecto correcto en el ERP)
              propiedadId: propPrincipalFull?.id || propPrincipal?.propiedadId || "",
              propiedadFolio: propPrincipal?.folio || "",
              propiedadDescripcion:
                propPrincipal?.alias ||
                propPrincipal?.nombre ||
                (propPrincipal
                  ? `Mz ${propPrincipal.manzana} Lt ${propPrincipal.lote}`
                  : ""),
              fraccionamientoId: propPrincipalFull?.fraccionamientoId || propPrincipal?.fraccionamientoId || "",
              fraccionamientoNombre: propPrincipalFull?.fraccNombre || propPrincipalFull?.fraccionamientoNombre || "",
              proyectoId: propPrincipalFull?.proyectoId || "",
              proyectoNombre: propPrincipalFull?.proyectoNombre || "",
              obraId: propPrincipalFull?.obraId || "",
              obraNombre: propPrincipalFull?.obraNombre || "",
              comisionistaId: cd.comisionistaId,
              comisionistaNombre: cd.comisionistaNombre,
              tipoCalculo: cd.tipoCalculo,
              porcentaje: cd.porcentaje,
              montoBase: monto,
              montoTotal: cd.tipoCalculo === "monto_fijo" ? cd.montoFijo : undefined,
              esquemaPago: cd.esquema,
              mensualidadesN: cd.mensualidadesN,
              hitosCustom: cd.hitosCustom,
            });
          } catch (comErr) {
            console.error("Error creando comisión:", comErr);
            notify.warn(`Deuda creada, pero no se pudo registrar la comisión: ${comErr.message}`);
          }
          } // ← cierre del else (planValido)
        }

        // ── Aplicación de la señal del apartado (si venimos convirtiendo) ──
        // La señal ya está en la caja del proyecto (registrada al apartar).
        // Aplicarla como pago tipo "enganche" en la nueva deuda — sin crear
        // otro movimiento de caja (la salida → entrada ya quedó al apartar).
        let senialAplicada = 0;
        if (enModoConversion && apartadoOrigen?.senial?.estado === "retenida"
            && apartadoOrigen.senial.monto > 0) {
          senialAplicada = Number(apartadoOrigen.senial.monto || 0);
          try {
            await processPayment(
              deudaCreadaId,
              senialAplicada,
              apartadoOrigen.senial.metodo || "efectivo",
              {
                fecha: form.fecha,
                notas: `Aplicación de señal del apartado ${apartadoOrigen.id}`,
                tipo: "enganche",
                crearMovimientoCaja: false, // ya fue registrado al crear el apartado
                referencia: apartadoOrigen.senial.referencia || "",
              }
            );
          } catch (sErr) {
            console.error("Error aplicando señal:", sErr);
            notify.warn(`Deuda creada, pero no se pudo aplicar la señal: ${sErr.message}`);
            senialAplicada = 0;
          }
        }

        // ── Enganche cobrado al firmar ─────────────────────────────────────
        // Registra el pago del enganche (descontando lo ya aplicado por señal)
        // para que el devengo pueda disparar el hito "enganche.cobrado".
        const engancheRestante = Math.max(enganche - senialAplicada, 0);
        if (form.enganchemodo === "al_firmar" && engancheRestante > 0) {
          try {
            await processPayment(deudaCreadaId, engancheRestante, form.metodoEnganche, {
              fecha: form.fecha,
              notas: senialAplicada > 0
                ? `Enganche al firmar (resto tras aplicar señal de ${senialAplicada})`
                : "Enganche cobrado al firmar contrato",
              tipo: "enganche",
            });
          } catch (engErr) {
            console.error("Error registrando enganche al firmar:", engErr);
            notify.warn(
              `Deuda creada, pero no se pudo registrar el pago del enganche: ${engErr.message}`
            );
          }
        }

        // ── Devengo automático al crear ────────────────────────────────────
        // Evalúa "contrato.firmado" (esquemas escalonados) y "enganche.cobrado"
        // (si se registró el pago arriba). EST-A.1 patron: await + try/catch +
        // notify para que el operador se entere si la creacion de CxP falla.
        if (cd.activa && cd.comisionistaId) {
          try {
            const { devengados, errores } = await procesarDevengoComision(deudaCreadaId);
            if (devengados > 0) {
              notify.success(
                `${devengados} hito${devengados > 1 ? "s" : ""} de comisión devengado${devengados > 1 ? "s" : ""} al crear la deuda.`
              );
            }
            if (errores.length > 0) {
              console.error("procesarDevengoComision errores:", errores);
              notify.error(
                `Deuda creada, pero el devengo de comisiones reportó errores: ${errores.slice(0, 2).join(" | ")}`
              );
            }
          } catch (e) {
            console.error("Devengo al crear deuda:", e);
            notify.error(
              `Deuda creada, pero el devengo falló: ${e?.message || "error"}. Revisa manualmente desde el detalle.`
            );
          }
        }

        // ── Cierre del apartado si venimos en modo conversión ──────────────
        // Marca el apartado como "convertido" enlazando la deudaId.
        // Fire-and-forget — si falla, la deuda ya quedó creada y la propiedad
        // ya transicionó a "vendida"; el apartado huérfano puede limpiarse
        // manualmente sin afectar el ERP.
        if (enModoConversion && apartadoOrigen?.id) {
          marcarApartadoComoConvertido(apartadoOrigen.id, deudaCreadaId).catch((e) =>
            console.warn("marcarApartadoComoConvertido:", e)
          );
        }

        notify.success(
          enModoConversion
            ? "Apartado convertido a deuda correctamente."
            : "Deuda creada correctamente"
        );
        navigate(`/app/cliente/${id}/deudas`);
      } catch (err) {
        console.error(err);
        notify.error(err.message);
        setSaving(false);
        return;
      }

      setSaving(false);
    },
    [saving, propSelList, form, cliente, id, navigate, can, fechaVencimientoApartado, enModoConversion, apartadoOrigen]
  );

  if (!can("deudas.crear")) {
    return <AccessDenied message="No tienes permiso para crear deudas." />;
  }

  const esApartado = form.modoAlta === "apartado";
  const mostrarEngancheModo =
    !esApartado && Number(form.enganche) > 0;

  return (
    <div className="agregar-deuda-container">
      <div className="agregar-deuda-header">
        <p>{esApartado ? "Apartado" : "Deuda"}</p>
        <h2>{esApartado ? "Apartar Propiedad" : "Registrar Nueva Deuda"}</h2>
        {cliente && (
          <span>
            {cliente.nombre || ""} {cliente.apellidos || ""}
          </span>
        )}
      </div>

      <form onSubmit={guardar} className="form-deuda">

        {/* ── Banner cuando se convierte un apartado ──────────────────── */}
        {enModoConversion && apartadoOrigen && (
          <div className="conversion-apartado-banner">
            <strong>Convirtiendo apartado</strong>
            <span>
              {apartadoOrigen.propiedades?.length || 0} propiedad
              {(apartadoOrigen.propiedades?.length || 0) === 1 ? "" : "es"} preseleccionada
              {(apartadoOrigen.propiedades?.length || 0) === 1 ? "" : "s"}.
              Apartado creado el{" "}
              {toJsDate(apartadoOrigen.fechaApartado)?.toLocaleDateString("es-MX") || "—"}
              .
            </span>
          </div>
        )}

        {/* ── Selector de tipo de operación (oculto en conversión) ────── */}
        {!enModoConversion && (
          <div className="campo campo-modo-alta">
            <label>Tipo de operación</label>
            <div className="modo-alta-tabs">
              <button
                type="button"
                className={`modo-tab${!esApartado ? " active" : ""}`}
                onClick={() => setForm((f) => ({ ...f, modoAlta: "deuda" }))}
              >
                Venta / Deuda
              </button>
              <button
                type="button"
                className={`modo-tab${esApartado ? " active" : ""}`}
                onClick={() => setForm((f) => ({ ...f, modoAlta: "apartado" }))}
              >
                Apartar (30 días)
              </button>
            </div>
            {esApartado && (
              <p className="modo-hint">
                La propiedad quedará reservada sin compromiso de pago. Si no se formaliza en
                30 días, se libera automáticamente.
              </p>
            )}
          </div>
        )}

        {/* ── Concepto ──────────────────────────────────────────────────── */}
        <div className="campo">
          <label>{esApartado ? "Descripción del apartado" : "Concepto"}</label>
          <input
            name="concepto"
            value={form.concepto}
            onChange={onChange}
            placeholder={esApartado ? "Descripción breve del apartado" : "Concepto de la deuda"}
          />
        </div>

        {/* ── Campos de venta proyectada ───────────────────────────────────
            Visibles SIEMPRE — en modo apartado se guardan como
            "ventaProyectada" para pre-llenar al convertir.
        */}
        <>
          <div className="campo">
            <label>
              Monto {esApartado && <small style={{ color: "#92400e", fontWeight: 600 }}>(precio venta proyectado)</small>}
            </label>
            <input
              name="monto"
              type="number"
              value={form.monto}
              onChange={onChange}
              placeholder="Monto total"
            />
          </div>

          <div className="campo">
            <label>Enganche</label>
            <input
              name="enganche"
              type="number"
              value={form.enganche}
              onChange={onChange}
              placeholder="Enganche"
            />
          </div>

          {/* ── Modo de cobro del enganche — solo en modo deuda ───────────── */}
          {mostrarEngancheModo && (
            <div className="campo campo-enganche-modo">
              <label>¿Cómo se cobra el enganche?</label>
              <div className="enganche-modo-opts">
                <label className={`opt-label${form.enganchemodo === "al_firmar" ? " active" : ""}`}>
                  <input
                    type="radio"
                    name="enganchemodo"
                    value="al_firmar"
                    checked={form.enganchemodo === "al_firmar"}
                    onChange={onChange}
                  />
                  Cobrado al firmar — registrar el pago ahora
                </label>
                <label className={`opt-label${form.enganchemodo === "diferido" ? " active" : ""}`}>
                  <input
                    type="radio"
                    name="enganchemodo"
                    value="diferido"
                    checked={form.enganchemodo === "diferido"}
                    onChange={onChange}
                  />
                  Se cobrará por separado (diferido)
                </label>
              </div>
              {form.enganchemodo === "diferido" && Number(form.enganche || 0) > 0 && (
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    name="engancheCobradoFuera"
                    checked={!!form.engancheCobradoFuera}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, engancheCobradoFuera: e.target.checked }))
                    }
                  />
                  <span>
                    El enganche <strong>ya se cobró por fuera</strong> del sistema (efectivo / transferencia previa)
                    <small style={{ display: "block", color: "#64748b", marginTop: 2 }}>
                      Si NO marcas esto, el enganche queda como pactado pendiente — no se descuenta del saldo hasta que registres el pago.
                    </small>
                  </span>
                </label>
              )}
              {form.enganchemodo === "al_firmar" && (
                <div className="enganche-metodo-row">
                  <label style={{ margin: 0, fontWeight: 700, fontSize: "0.85rem", color: "#334155" }}>
                    Método de pago del enganche
                  </label>
                  <select
                    name="metodoEnganche"
                    value={form.metodoEnganche}
                    onChange={onChange}
                  >
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia bancaria</option>
                    <option value="cheque">Cheque</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
              )}
            </div>
          )}

          <div className="campo">
            <label>Mensualidad</label>
            <input
              name="mensualidad"
              type="number"
              value={form.mensualidad}
              onChange={onChange}
              placeholder="Mensualidad"
            />
          </div>

          <div className="campo">
            <label>Plazo</label>
            <input
              name="plazo"
              type="number"
              value={form.plazo}
              onChange={onChange}
              placeholder="Plazo"
            />
          </div>

          <div className="campo">
            <label>
              {esApartado ? "Fecha tentativa de inicio" : "Fecha de inicio"}
            </label>
            <input
              name="fecha"
              type="date"
              value={form.fecha}
              onChange={onChange}
            />
          </div>

          <div className="campo">
            <label>Referencia</label>
            <input
              name="referencia"
              value={form.referencia}
              onChange={onChange}
              placeholder="Referencia"
            />
          </div>

          <div className="campo checkbox">
            <label>
              <input
                type="checkbox"
                name="fueraPais"
                checked={form.fueraPais}
                onChange={onChange}
              />
              Cliente fuera del país regularmente
            </label>
          </div>

          {form.fueraPais && (
            <div className="campo">
              <label>Responsable</label>
              <input
                value={searchResp}
                onChange={(e) => setSearchResp(e.target.value)}
                placeholder="Buscar responsable"
              />

              {searchResp && (
                <div className="lista-sugerencias">
                  {responsablesFiltrados.map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      onClick={() => selectResponsable(c)}
                      className="item-sugerencia"
                    >
                      {`${c.nombre || ""} ${c.apellidos || ""}`.trim() || c.id}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </>

        {/* ── Buscar propiedades (ambos modos) ────────────────────────────── */}
        <div className="campo">
          <label>Buscar propiedades</label>
          <input
            value={propQuery}
            onChange={(e) => setPropQuery(e.target.value)}
            placeholder="Buscar por folio, ubicación, nombre, mz/lt..."
          />

          {propQuery && (
            <div className="lista-sugerencias">
              {filteredProps.map((p) => {
                const selected = propSelList.some((x) => x.id === p.id);
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => selectProp(p)}
                    className={`item-sugerencia ${selected ? "seleccionado" : ""}`}
                  >
                    {p.folio} — {p.nombre || p.alias || `Mz ${p.manzana} Lt ${p.lote}`}
                    {p.ubicacion ? ` — ${p.ubicacion}` : ""}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {propSelList.length > 0 && (
          <div className="campo">
            <label>Propiedades seleccionadas</label>
            <div className="seleccionados-wrap">
              {propSelList.map((p) => (
                <div key={p.id} className="seleccionado-chip">
                  <span>
                    {p.folio} — {p.nombre || p.alias || `Mz ${p.manzana} Lt ${p.lote}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeProp(p.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Bloque exclusivo del apartado: señal + info de vencimiento ── */}
        {esApartado && (
          <div className="campo apartado-info-block">
            <div className="apartado-vence-badge">
              La propiedad quedará <b>apartada</b> hasta el{" "}
              <b>{fechaVencimientoApartado}</b> (30 días). Si no se formaliza,
              se liberará automáticamente.
            </div>

            {/* Señal / depósito */}
            <div className="apartado-senial-grid">
              <div>
                <label>Señal recibida (opcional)</label>
                <input
                  name="senialMonto"
                  type="number"
                  value={form.senialMonto}
                  onChange={onChange}
                  placeholder="Monto de la señal"
                  min="0"
                  step="any"
                />
              </div>
              <div>
                <label>Método de la señal</label>
                <select
                  name="senialMetodo"
                  value={form.senialMetodo}
                  onChange={onChange}
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="cheque">Cheque</option>
                  <option value="tarjeta">Tarjeta</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div>
                <label>Referencia (opcional)</label>
                <input
                  name="senialReferencia"
                  value={form.senialReferencia}
                  onChange={onChange}
                  placeholder="Folio, núm. cheque, etc."
                />
              </div>
            </div>
            {Number(form.senialMonto) > 0 && (
              <p className="apartado-senial-hint">
                Se registrará un <b>movimiento de entrada</b> en la caja del proyecto.
                Al convertir el apartado, la señal se aplicará como pago tipo "enganche".
              </p>
            )}

            <label style={{ marginTop: 10 }}>Notas del apartado (opcional)</label>
            <textarea
              name="notas"
              value={form.notas}
              onChange={onChange}
              placeholder="Observaciones, condiciones pactadas…"
              rows={3}
            />
          </div>
        )}

        {/* ── Sección comisión — visible en ambos modos.
             En modo apartado se guarda como comisionProyectada y se reutiliza
             al convertir. Al convertir, pasamos initialData con la comisión
             proyectada para pre-llenar el sub-componente.
        */}
        <div style={{ gridColumn: "1 / -1" }}>
          {/* Esperar a tener los datos del apartado antes de montar SeccionComision
              cuando estamos en modo conversión (evita reset al cargar) */}
          {(!enModoConversion || apartadoOrigen) && (
            <SeccionComision
              key={enModoConversion ? `apar-${apartadoOrigen?.id}` : "nueva-deuda"}
              montoDeuda={Number(form.monto) || 0}
              engancheDeuda={Number(form.enganche) || 0}
              mensualidadDeuda={Number(form.mensualidad) || 0}
              initialData={
                enModoConversion ? apartadoOrigen?.comisionProyectada || null : null
              }
              onChange={(data) => { comisionDataRef.current = data; }}
            />
          )}
        </div>

        <button type="submit" disabled={saving || !propSelList.length}>
          {saving
            ? "Guardando…"
            : esApartado
            ? "Apartar propiedad"
            : "Guardar deuda"}
        </button>
      </form>

      <button
        className="volver-btn"
        onClick={() => navigate(`/app/cliente/${id}`)}
      >
        Cancelar / Volver al Cliente
      </button>
    </div>
  );
}
